import fetch from 'node-fetch';
import { DetailShopV2Props } from '../components/ShopV2/Detail';
import { ManagementStoreV2Type } from '../components/ShopV2/Management';
import { RequirementsProps } from './context';
import { Disability } from './tools';
import { updateSellerStatus } from './ciblerAPI';

export type StoreInfoProvider = 'apidae';

const langs = [
  { key: 'fr', objectKey: 'libelleFr' },
  { key: 'en', objectKey: 'libelleEn' },
  { key: 'de', objectKey: 'libelleDe' },
  { key: 'nl', objectKey: 'libelleNl' },
  { key: 'it', objectKey: 'libelleIt' },
  { key: 'es', objectKey: 'libelleEs' },
  { key: 'ru', objectKey: 'libelleRu' },
  { key: 'zh', objectKey: 'libelleZh' },
  { key: 'pt-br', objectKey: 'libellePtBr' },
  { key: 'ja', objectKey: 'libelleJa' },
];

type CustomDataStoreLang = (typeof langs)[number]['key'];
export type CustomDataStoreWithLangs = {
  [key in CustomDataStoreLang]: CustomDataStore;
};

export interface CustomDataStore {
  store?: {
    name?: string;
    description?: string;
    address?: {
      address1?: string;
      address2?: string;
      postalCode?: string;
      city?: string;
      country?: string;
      latitude?: number;
      longitude?: number;
    };
    phone?: string;
    email?: string;
    website?: string;
    schedule?: string;
    facebook?: string;
    instagram?: string;
    twitter?: string;
    tiktok?: string;
    pinterest?: string;
    youtube?: string;
    tarifs?: string;
    minimumPrice?: number;
    maximumPrice?: number;
    management?: ManagementStoreV2Type;
    images?: ImageType[];
    handicaps?: Disability[];
    labels?: {
      id: number;
      logoUrl: string;
      label: string;
    }[];
  };
  groups?: CustomStoreGroup[];
}

export interface CustomStoreGroup {
  title?: string;
  anchor?: string;
  items?: CustomStoreItem[];
}

type CustomStoreItem =
  | TextStoreItem
  | TextListStoreItem
  | ImageListStoreItem
  | TableStoreItem
  | DetailsStoreItem;

export interface TextStoreItem {
  type: 'text';
  text: string; // dangerouslySetInnerHTML
}

export interface TextListStoreItem {
  type: 'textList';
  withBullets?: boolean;
  texts: string[]; // dangerouslySetInnerHTML
}

export interface ImageType {
  url: string;
  alt: string;
}

export interface ImageListStoreItem {
  type: 'imageList';
  images: ImageType[];
}

export interface TableStoreItem {
  type: 'table';
  title?: string;
  rows: string[][]; // dangerouslySetInnerHTML
}

export interface DetailsStoreItem {
  type: 'details';
  details: DetailShopV2Props[];
}

type GetStoreInfoFromProviderFn = (
  requirements: RequirementsProps,
  provider: StoreInfoProvider,
  id: string,
  sellerId?: string | number
) => Promise<CustomDataStoreWithLangs>;
const getStoreInfoFromProvider: GetStoreInfoFromProviderFn = async (requirements, provider, id, sellerId) => {
  switch (provider) {
    case 'apidae':
      return await getStoreInfoFromApidae(id, requirements.config?.apidae, requirements, sellerId);
    default:
      return {} as CustomDataStoreWithLangs;
  }
};

interface ApidaeConfig {
  projectId: number;
  apiKey: string;
}

type GetStoreInfoFromApidaeFn = (
  id: string,
  config: ApidaeConfig,
  requirements: RequirementsProps,
  sellerId?: string | number
) => Promise<CustomDataStoreWithLangs>;
const getStoreInfoFromApidae: GetStoreInfoFromApidaeFn = async (id, config, requirements, sellerId) => {
  const { projectId, apiKey } = config || {};

  try {
    const apidaeData = await fetch(
      `https://api.apidae-tourisme.com/api/v002/objet-touristique/get-by-id/${id}?responseFields=@all&locales=fr,en,de,nl,it,es,ru,zh,pt-br,ja&apiKey=${apiKey}&projetId=${projectId}`
    );
    
    // Vérifier si la réponse est 404
    if (apidaeData.status === 404) {
      console.error(`[APIDAE] Erreur 404: POI avec l'ID ${id} introuvable dans APIDAE et sellerId ${sellerId}`);
      
      // Mettre à jour le statut du seller dans CIBLER si sellerId est disponible
      if (sellerId) {
        await updateSellerStatus(sellerId, requirements);
      }
      
      return {} as CustomDataStoreWithLangs;
    }
    
    const data = await apidaeData.json();
    console.log('data', data);
    
    // Vérifier si les données POI sont présentes
    if (!data || !data.id || !data.nom) {
      console.error(`[APIDAE] Données POI manquantes ou invalides pour l'ID ${id}:`, {
        hasData: !!data,
        hasId: !!data?.id,
        hasNom: !!data?.nom,
        data: data
      });
      return {} as CustomDataStoreWithLangs;
    }

    const customDataWithLangs: CustomDataStoreWithLangs = langs.reduce(
      (acc, { key: lang, objectKey: key }) => {
        const getMoyenCommunication = (type: string) =>
          data?.informations?.moyensCommunication?.find((c: any) => c?.type?.libelleFr === type)
            ?.coordonnees?.fr ?? null;

        let minimumPrice = data?.descriptionTarif?.gratuit
          ? 0
          : Math.min(
              ...(data?.descriptionTarif?.periodes?.flatMap((p: any) =>
                p.tarifs
                  ?.filter(
                    (t: any) =>
                      t.type?.libelleFr !== 'Taxe de séjour' &&
                      t.type?.libelleFr !== 'Petit déjeuner'
                  )
                  ?.map((t: any) => t.minimum || Infinity)
              ) ?? [Infinity])
            );
        if (minimumPrice === Infinity) minimumPrice = null;

        let maximumPrice = Math.max(
          ...(data?.descriptionTarif?.periodes?.flatMap((p: any) =>
            p.tarifs?.map((t: any) => t.maximum || 0)
          ) ?? [0])
        );
        if (maximumPrice === 0) maximumPrice = null;

        let customData: CustomDataStore = {
          store: {
            name: data?.nom?.[key] || data?.nom?.libelleFr,
            description:
              data?.presentation?.descriptifDetaille?.[key] ??
              data?.presentation?.descriptifDetaille?.libelleFr ??
              data?.presentation?.descriptifCourt?.[key] ??
              data?.presentation?.descriptifCourt?.libelleFr,
            address: {
              address1: data?.localisation?.adresse?.adresse1,
              address2: data?.localisation?.adresse?.adresse2,
              postalCode: data?.localisation?.adresse?.codePostal,
              city: data?.localisation?.adresse?.commune?.nom,
              country:
                data?.localisation?.adresse?.commune?.pays?.[key] ??
                data?.localisation?.adresse?.commune?.pays?.libelleFr,
              latitude: data?.localisation?.geolocalisation?.geoJson?.coordinates[1],
              longitude: data?.localisation?.geolocalisation?.geoJson?.coordinates[0],
            },
            phone: getMoyenCommunication('Téléphone'),
            email: getMoyenCommunication('Mél'),
            website: getMoyenCommunication('Site web (URL)'),
            schedule:
              data?.ouverture?.periodeEnClair?.[key] ?? data?.ouverture?.periodeEnClair?.libelleFr,
            tarifs: data?.descriptionTarif?.tarifsEnClair?.[key],
            minimumPrice,
            maximumPrice,
            management: {
              name: data?.gestion?.membreProprietaire?.nom,
              email: data?.gestion?.signalerUnProblemeMails,
              website: data?.gestion?.membreProprietaire?.siteWeb,
              updatedAt: data?.gestion?.dateModification,
              createdAt: data?.gestion?.dateCreation,
            },
            images: data?.illustrations?.slice(0, 10).map((i: any) =>
              i?.traductionFichiers?.[0]
                ? {
                    url: i.traductionFichiers[0].urlQHD || i.traductionFichiers[0].url,
                    alt: i.traductionFichiers[0].fileName,
                  }
                : null
            ),
            handicaps: (data?.prestations?.labelsTourismeHandicap || [])
              .filter((item) => [1224, 1225, 1226, 1227].includes(item.id))
              .map((item) => {
                const handicapMap = {
                  1224: 'AUDITIF',
                  1225: 'MENTAL',
                  1226: 'MOTEUR',
                  1227: 'VISUEL',
                };
                return handicapMap[item.id] as Disability;
              }),
            labels: [],
          },
          groups: [],
        };

        const availablePictosIds = new Set([
          1224, 1225, 1226, 1227, 1593, 1598, 1604, 1664, 1680, 1687, 1688, 1690, 2541, 2543, 2546,
          2548, 2561, 2612, 2614, 2615, 2616, 2627, 2628, 2629, 2630, 2631, 2634, 2635, 2636, 2638,
          2639, 2658, 2659, 2660, 2661, 2662, 2669, 2677, 2679, 2702, 2708, 2718, 2719, 2721, 2724,
          2730, 2737, 2738, 2748, 2749, 2750, 2751, 2758, 2765, 2767, 2769, 2770, 2771, 2773, 2775,
          2779, 2787, 2790, 2792, 2806, 2808, 2810, 2823, 2827, 2833, 2837, 2840, 2846, 2849, 2853,
          2854, 2855, 2870, 2872, 2873, 2915, 2917, 2919, 2921, 2943, 2944, 2954, 2956, 2958, 2960,
          2967, 3255, 3261, 3263, 3264, 3265, 3473, 3474, 3475, 3479, 3480, 3484, 3574, 3575, 3576,
          3584, 3585, 3586, 3587, 3588, 3602, 3604, 3605, 3606, 3607, 3612, 3613, 3614, 3615, 3616,
          3631, 3640, 3641, 3642, 3643, 3739, 3785, 3826, 3844, 3980, 4179, 4460, 4462, 4464, 4483,
          4515, 4531, 4593, 4629, 4822, 4823, 4824, 4825, 4826, 4827, 4872, 4884, 4927, 5027, 5028,
          5089, 5092, 5178, 5214, 5257, 5282, 5309, 5317, 5320, 5395, 5396, 5397, 5398, 5399, 5442,
          5525, 5529, 5564, 5647, 5682, 5685, 5886, 6049, 6311, 6312, 6313, 6314, 6315, 6316, 6317,
          6318, 6319, 6323, 6326, 6334, 6335, 6365, 6398, 6677, 7079, 7144, 5625, 4540, 4926,
        ]);

        // Debug Picto Position
        /*
        const stringData = JSON.stringify(data);
        availablePictosIds.forEach((id) => {
          if (stringData.includes(`"id":${id},`)) {
            console.log(
              `Picto ${id}:`,
              data,
              `Position: ${stringData.indexOf(`"id":${id}`)}`,
              `Around text (-50, +50): ${stringData.slice(stringData.indexOf(`"id":${id}`) - 50, stringData.indexOf(`"id":${id}`) + 50)}`
            );
          }
        });
        */

        customData.store.labels = (
          [
            ...(data?.informationsRestauration?.chaines ?? []),
            ...(data?.informationsRestauration?.classementsGuides ?? []),
            ...(data?.informationsHotellerie?.chaines ?? []),
            data?.informationsHebergementLocatif?.classementPrefectoral,
            data?.informationsHotellerie?.classement,
            data?.informationsHotelleriePleinAir?.classement,
            ...(data?.presentation?.typologiesPromoSitra ?? []),
            ...(data?.informationsHebergementLocatif?.labels ?? []),
            ...(data?.informationsHebergementLocatif?.labels?.map((l: any) => l?.typeLabel) ?? []),
            ...(data?.informationsPatrimoineCulturel?.categories ?? []),
          ]
            ?.filter((l: any) => l?.id && availablePictosIds.has(l.id))
            ?.reduce(
              (acc: string[], c: any) => [
                ...acc,
                {
                  id: c.id,
                  logoUrl: `https://picto-label.charentestourisme.com/${c?.id}.svg`,
                  label: c?.[key],
                },
              ],
              []
            )
            ?.filter((l: number) => l) as CustomDataStore['store']['labels']
        )?.filter((l) => l);

        // Informations Hôtellerie:
        const infosHotel = data?.informationsHotellerie;
        if (infosHotel) {
          const texts = [
            !!infosHotel.capacite?.nombreChambreClasses
              ? `{{t:nombreChambreClasses}} : <strong>${infosHotel.capacite.nombreChambreClasses}</strong>`
              : null,
            !!infosHotel.capacite?.nombreTotalPersonnesReel
              ? `{{t:nombreTotalPersonnesReel}} : <strong>${infosHotel.capacite.nombreTotalPersonnesReel}</strong>`
              : null,
            !!infosHotel.capacite?.nombreChambresDoubles
              ? `{{t:nombreChambresDoubles}} : <strong>${infosHotel.capacite.nombreChambresDoubles}</strong>`
              : null,
            !!infosHotel.capacite?.nombreSuites
              ? `{{t:nombreSuites}} : <strong>${infosHotel.capacite.nombreSuites}</strong>`
              : null,
            !!infosHotel.capacite?.nombreChambresMobiliteReduite
              ? `{{t:nombreChambresMobiliteReduite}} : <strong>${infosHotel.capacite.nombreChambresMobiliteReduite}</strong>`
              : null,
            !!infosHotel.capacite?.nombreAppartements
              ? `{{t:nombreAppartements}} : <strong>${infosHotel.capacite.nombreAppartements}</strong>`
              : null,
          ].filter((t) => t);

          if (texts.length) {
            customData.groups.push({
              title: '{{t:hotelInformations}}',
              items: [
                {
                  type: 'textList',
                  texts,
                },
              ],
            });
          }
        }

        // Équipements et services:
        if (
          data?.prestations?.equipements?.length ||
          data?.prestations?.services?.length ||
          data?.prestation?.conforts?.length
        ) {
          customData.groups.push({
            title: '{{t:equipmentsAndServices}}',
            items: [
              {
                type: 'textList',
                withBullets: true,
                texts: [
                  ...(data?.prestations?.equipements?.map((e: any) => e?.[key]) ?? []),
                  ...(data?.prestations?.services?.map((s: any) => s?.[key]) ?? []),
                  ...(data?.prestations?.conforts?.map((c: any) => c?.[key]) ?? []),
                ],
              },
              data?.prestations?.complementAccueil?.[key]
                ? {
                    type: 'text',
                    text: `&#9432; ${data.prestations.complementAccueil[key]}`,
                  }
                : null,
            ],
          });
        }

        // Activités:
        if (data?.prestations?.activites?.length) {
          customData.groups.push({
            title: '{{t:activities}}',
            items: [
              {
                type: 'textList',
                withBullets: true,
                texts: data.prestations.activites.map((a: any) => a?.[key]),
              },
            ],
          });
        }

        // Informations Fête et Manifestations:
        if (data?.informationsFeteEtManifestation?.themes?.length) {
          customData.groups.push({
            title: '{{t:themes}}',
            items: [
              {
                type: 'textList',
                withBullets: true,
                texts: data.informationsFeteEtManifestation.themes.map((t: any) => t?.[key]),
              },
            ],
          });
        }

        // Environnement:
        if (data?.localisation?.environnements?.length) {
          customData.groups.push({
            title: '{{t:environment}}',
            items: [
              {
                type: 'textList',
                withBullets: true,
                texts: data.localisation.environnements.map((e: any) => e?.[key]),
              },
            ],
          });
        }

        // Langues parlées:
        if (data?.prestations?.languesParlees?.length) {
          customData.groups.push({
            title: '{{t:spokenLanguages}}',
            items: [
              {
                type: 'textList',
                withBullets: true,
                texts: data.prestations.languesParlees.map((l: any) => l?.[key]),
              },
            ],
          });
        }

        // Horaires:
        if (customData?.store?.schedule) {
          customData.groups.push({
            title: '{{t:schedules}}',
            anchor: 'schedules',
            items: [
              {
                type: 'textList',
                texts: customData.store.schedule.split('\n\n'),
              },
            ],
          });
        }

        // Tarifs:
        if (data?.descriptionTarif?.tarifsEnClair) {
          const tarifsTaxes = data?.descriptionTarif?.tarifsEnClair?.[key]?.split('\n\n\n');
          if (tarifsTaxes?.length) {
            customData.groups.push({
              title: '{{t:prices}}',
              anchor: 'prices',
              items: [
                {
                  type: 'table',
                  rows:
                    tarifsTaxes[0]
                      ?.split('\n')
                      ?.filter((t: string) => t)
                      ?.map((t: string) => (t.endsWith('.') ? t.slice(0, -1) : t))
                      ?.map((t: string) => t.split(': '))
                      ?.map((t: string[]) =>
                        t.length > 2
                          ? [
                              t[0],
                              `<strong>${t
                                ?.slice(1)
                                ?.join(' : ')
                                ?.replace(/\(([^)]+)\)\.?$/, (_, p) => `<p>(${p})</p>`)
                                ?.replace(
                                  /(€)([^<]*)$/,
                                  (match, euroSign, textAfterEuro) =>
                                    `${euroSign} <p>${textAfterEuro}</p>`
                                )}</strong>`,
                            ]
                          : [
                              t[0],
                              !!t[1]
                                ? `<strong>${t[1]
                                    .replace(/\(([^)]+)\)\.?$/, (_, p) => `<p>(${p})</p>`)
                                    .replace(
                                      /(€)([^<]*)$/,
                                      (match, euroSign, textAfterEuro) =>
                                        `${euroSign} <p>${textAfterEuro}</p>`
                                    )}</strong>`
                                : '',
                            ]
                      ) ?? [],
                },
                {
                  type: 'textList',
                  texts: [tarifsTaxes[1]],
                },
              ],
            });
          }
        }

        // Visites:
        if (
          data?.visites?.languesVisite ||
          data?.visites?.prestationsVisitesIndividuelles?.length ||
          data?.visites?.prestationsVisitesGroupees?.length
        ) {
          customData.groups.push({
            title: '{{t:visits}}',
            anchor: 'prices',
            items: [
              {
                type: 'table',
                rows: [
                  data?.visites?.languesVisite
                    ? [
                        data.visites.languesVisite?.length > 1
                          ? '{{t:visitSpokenLanguages}}'
                          : '{{t:visitSpokenLanguage}}',
                        `<strong>${data.visites.languesVisite?.map((l: any) => l?.[key])?.join(', ')}</strong>`,
                      ]
                    : null,
                  data?.visites?.prestationsVisitesIndividuelles?.length
                    ? [
                        '{{t:individualVisits}}',
                        `<strong>${data.visites.prestationsVisitesIndividuelles
                          ?.map((p: any) => p?.[key])
                          ?.join(', ')}</strong>`,
                      ]
                    : null,
                  data?.visites?.prestationsVisitesGroupees?.length
                    ? [
                        '{{t:groupedVisits}}',
                        `<strong>${data.visites.prestationsVisitesGroupees?.map((p: any) => p?.[key])?.join(', ')}</strong>`,
                      ]
                    : null,
                ],
              },
            ],
          });
        }

        // Moyens de paiement:
        if (data?.descriptionTarif?.modesPaiement?.length) {
          customData.groups.push({
            title: '{{t:paymentMethods}}',
            items: [
              {
                type: 'textList',
                withBullets: true,
                texts: data.descriptionTarif.modesPaiement.map((p: any) => p?.[key]),
              },
            ],
          });
        }

        // Réseaux sociaux:
        if (data?.informations?.moyensCommunication?.length) {
          const socials = [
            { id: 'Page facebook', name: 'facebook', icon: 'facebookV2' },
            { id: 'Instagram', name: 'instagram', icon: 'instagramV2' },
            { id: 'Réseau social ex-Twitter', name: 'twitter', icon: 'twitter' },
            { id: 'TikTok', name: 'tiktok', icon: 'tiktok' },
            { id: 'Pinterest', name: 'pinterest', icon: 'pinterest' },
            { id: 'Chaîne Youtube', name: 'youtube', icon: 'youtubeV2' },
          ];

          const socialGroup = {
            title: '{{t:socialNetworks}}',
            items: [
              {
                type: 'details',
                details: socials
                  .map(({ id, name, icon }) => {
                    const value = getMoyenCommunication(id);
                    if (!value) return null;

                    return {
                      icon: icon ?? name,
                      title: name,
                      value,
                      link: { href: value },
                    } as DetailShopV2Props;
                  })
                  ?.filter((d) => d),
              } as DetailsStoreItem,
            ],
          };

          if (socialGroup.items[0]?.details.length) customData.groups.push(socialGroup);
        }

        acc[lang] = customData;
        return acc;
      },
      {} as CustomDataStoreWithLangs
    );

    // Remove the duplicates langs (same description[:50])
    const customDataWithLangsFiltered: CustomDataStoreWithLangs = {};
    Object.keys(customDataWithLangs).forEach((lang) => {
      const description = customDataWithLangs[lang]?.store?.description;
      if (!description && !customDataWithLangs[lang]?.groups?.length) return;

      const isDuplicate = Object.keys(customDataWithLangsFiltered).some(
        (l) => customDataWithLangsFiltered[l]?.store?.description === description
      );
      if (!isDuplicate) customDataWithLangsFiltered[lang] = customDataWithLangs[lang];
    });

    return customDataWithLangsFiltered;
  } catch (error) {
    console.error('Error while fetching data from Apidae', error);
    return {} as CustomDataStoreWithLangs;
  }
};

export { getStoreInfoFromProvider };
